import { yupResolver } from '@hookform/resolvers/yup';
import { Container } from '@mui/material';
import momentTimezone from 'moment-timezone';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams, createSearchParams, useNavigate } from 'react-router-dom';
import { reactLocalStorage } from 'reactjs-localstorage';

import Breadcrumbs from 'commons/Breadcrumbs';
import Text from 'commons/Text';
import API from 'configs/API';
import { RequestCertificateProvider } from 'contexts/RequestCertificateContext';
import useInviteAndTrackingInvitation from 'hooks/useInviteAndTrackingInvitation';
import useUpdateArtwork from 'hooks/useUpdateArtwork';
import useArtwork from 'react-query/hooks/queries/useArtwork';
import Routes from 'utils/Route';
import { isValidEmailAddress, isValidPhoneNumber } from 'utils/Validation';
import { axiosPost } from 'utils/apis/axios';
import { generateStrongPassword, ToastTopHelper } from 'utils/utils';

import CertificateInfoStep from './components/CertificateInfoStep';
import useStyles from './styles';
import { certificateSchema } from './validations';

const CreateCertificate = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const user = useSelector((state) => state.auth.account.user);
  const { id } = useParams();
  const { artwork, isPending } = useArtwork(id);
  const { handleInviteUser } = useInviteAndTrackingInvitation();
  const tz = momentTimezone.tz.guess();

  const { mutateAsync: updateArtwork } = useUpdateArtwork(id);
  const { has_certificate } = artwork || {};
  const storedDataOwner = reactLocalStorage.get(`ownerInfo-${id}`);

  const certificateFormState = useForm({
    defaultValues: {},
    mode: 'onChange',
    resolver: yupResolver(certificateSchema),
  });

  const { reset } = certificateFormState;

  useEffect(() => {
    if (artwork) {
      reset({
        titleArtwork: artwork.title,
        height: artwork.size?.height,
        width: artwork.size?.width,
        depth: artwork.size?.depth,
        yearCreated: artwork.year_created,
        periodYear: artwork.period_created,
        mediumArtWork: artwork.medium,
        edition: null,
        piece: artwork.piece,
        owner: storedDataOwner ? JSON.parse(storedDataOwner)?.owner : null,
        newOwner: storedDataOwner ? JSON.parse(storedDataOwner)?.newOwner : null,
        contractNumber: storedDataOwner ? JSON.parse(storedDataOwner)?.contractNumber : '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artwork, reset]);

  const { handleSubmit } = certificateFormState;

  const registerNewUser = async (formData) => {
    const { success, data } = await axiosPost(API.AUTH.REGISTER_EMAIL, formData);

    if (success) {
      return data.user;
    }
    return null;
  };

  const handleRequestCertificate = async (formData, infoNewUser, dataOwner) => {
    const { success, data } = await axiosPost(API.ARTWORK.CREATE_CERTIFICATE(id), formData);

    if (success) {
      const isEmail = isValidEmailAddress(dataOwner?.newOwner?.contactInfo);

      if (dataOwner?.newOwner && isEmail) {
        handleInviteUser({
          recipient: infoNewUser?.user,
          password: infoNewUser?.password,
          certificateId: data?.id,
        });
      }

      ToastTopHelper.success(t('request_create_certificate_successfully'));
      const searchParams = createSearchParams({ certificate: 'show' });

      navigate({
        pathname: Routes.ArtworkDetail(data?.code),
        search: searchParams.toString(),
      });
    }
  };

  const handleCreateCer = async (data) => {
    let newUser = null;

    const password = generateStrongPassword();

    if (data?.newOwner) {
      const isEmail = isValidEmailAddress(data?.newOwner?.contactInfo);
      const isPhone = isValidPhoneNumber(data?.newOwner?.contactInfo);

      const formRegisterUser = {
        role: data?.newOwner?.role,
        password1: password,
        password2: password,
        time_zone: tz,
        name: data?.newOwner?.name,
        year_of_birth: data?.newOwner?.yearOfBirth,
        place_of_birth: data?.newOwner?.address,
      };

      if (isEmail) {
        formRegisterUser.email = data?.newOwner?.contactInfo;
      }

      if (isPhone) {
        formRegisterUser.phone_number = data?.newOwner?.contactInfo;
      }

      newUser = await registerNewUser(formRegisterUser);
      if (!newUser) {
        return;
      }
    }

    const formData = {
      owner_info: {
        user: newUser?.id || data?.owner?.id,
        address: data?.address,
        contract_number: data?.contractNumber,
      },
      edition_id: data.edition.id,
      issued_to_id: newUser?.id || data?.owner?.id,
    };

    const infoNewUser = {
      user: newUser,
      password: password,
    };

    handleRequestCertificate(formData, infoNewUser, data);
  };

  const handleUpdateArtwork = async (data) => {
    if (!has_certificate) {
      await updateArtwork({
        title: data?.titleArtwork,
        size: {
          height: data?.height,
          width: data?.width,
          depth: data?.depth,
        },
        medium: data?.mediumArtWork,
        year_created: data?.yearCreated,
        period_created: data?.periodYear,
        total_edition: data?.totalEdition,
        piece: data?.piece,
      });
    }
    await handleCreateCer(data);
  };

  const breadCrumbs = [
    {
      to: Routes.Home,
      text: t('home'),
    },
    {
      to: Routes.GalleryDetail(user?.uuid),
      text: t('gallery'),
    },
    {
      text: t('create_certificate'),
      active: true,
    },
  ];

  useEffect(() => {
    return () => {
      reactLocalStorage.remove(`ownerInfo-${id}`);
      reactLocalStorage.remove(`edition-${id}`);
    };
  }, [id]);

  if (isPending) {
    return null;
  }

  return (
    <main className={classes.wrapPage}>
      <Container>
        <Breadcrumbs items={breadCrumbs} />
        <Text
          variant="h1"
          className={classes.heading}
          type="2xl"
          fontWeightBold
        >
          {t('create_certificate')}
        </Text>

        <RequestCertificateProvider>
          <CertificateInfoStep
            artwork={artwork}
            onSubmit={handleSubmit(handleUpdateArtwork)}
            isPending={isPending}
            certificateFormState={certificateFormState}
          />
        </RequestCertificateProvider>
      </Container>
    </main>
  );
};

export default CreateCertificate;
